import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import { focusCity } from "src/store/ui"
import { selectIsReviewing } from "src/store/game"
import { cellDisplayName, getPlace } from "src/utils/maps"

import UnitsCanvas from "src/components/UnitsCanvas"
import UnderConstruction from "src/components/UnderConstruction"
import { buildings as buildingArt } from "src/assets/images"

import "./UnitsCanvasDetailed.css"

// Codes are snake_case on the wire and the wording is camelCase, the same mapping `BuildingModal`
// makes. One line rather than a shared helper, since that is all it is.
const buildingNameKey = (code) => `city.buildings.${code.replace(/_([a-z])/g, (_, c) => c.toUpperCase())}`

/* One building on the roster card: the plate, the level in the top-right notch, the price bottom
   left, and a press that buys the level. While a level is on its way (owner, 2026-10-03) the tile
   is framed in orange, the pick at work stands where the price was, and a press takes the order
   back instead — nothing was paid, so nothing is refunded. */
const BuildingTile = ({ code, level, cost, currency, allowed, reason, upgrading, main = false, t, onUpgrade, onCancelUpgrade, suppressClickUntil = null }) => {
  const name = t(buildingNameKey(code), { defaultValue: code })
  const classes = ["row-section", "row-building"]
  if (main) classes.push("row-main-building")
  if (upgrading) classes.push("upgrading")
  else if (!allowed) classes.push("blocked")
  return (
    <button
      type="button"
      className={classes.join(" ")}
      disabled={!upgrading && !allowed}
      title={upgrading ? t("city.cancelUpgradeHint", { level: level + 1 }) : reason || t("game.troops.upgradeTitle", { building: name })}
      onClick={(event) => {
        // The card underneath opens the city; an upgrade is not a request to go and look at
        // one, and a press that did both would fly the map away from what it just bought.
        event.stopPropagation()
        // A finger that rested on the tile lifted the row (the phone's reorder, `CityItem`) and
        // the browser still sends a click when it comes up: that is the lift ending, not an order.
        if (suppressClickUntil && Date.now() < suppressClickUntil.current) return
        if (upgrading) onCancelUpgrade?.(code)
        else onUpgrade?.(code)
      }}
    >
      <img src={buildingArt[code]} alt="" className="row-section-art" />
      <span className="row-section-text">
        {/* The bare number (owner, 2026-09-10): it is the only number on the tile that is not a
            price, in the corner of the picture of the thing it counts. A zero reads as "not built
            yet". */}
        <span className="row-section-label">{level}</span>
        {upgrading ? (
          <span className="row-section-cost row-section-upgrading">
            <UnderConstruction level={level + 1} className="row-section-construction" decorative />
          </span>
        ) : (
          /* No arrow, and no space before the coin: the tile is a button with a level written
             above the number, so what the number is cannot be read two ways. */
          <span className="row-section-cost">
            {cost ?? "?"}
            {currency}
          </span>
        )}
      </span>
    </button>
  )
}

const UnitsCanvasDetailed = ({
  units,
  city = null,
  held = true,
  productionTime = null,
  suppressClickUntil = null,
  // The roster's three marks (see TroopsTab). Off by default: the same row draws a city modal's
  // production preview, where none of them applies.
  underAttack = false,
  burning = false,
  isCrown = false,
  /* The city's main building — command centre or hatchery — under the coordinates, where the
     city's own identity is (owner, 2026-09-10). It is the one every other level is measured
     against, so it belongs with the name rather than in the rank of the other two.

     Shaped by `TroopsTab`, which is where the rules live; this only draws it. */
  mainBuilding = null,
  /* The other two a city grows on, priced and upgradable from the row itself (owner, 2026-09-09:
     "so we don't need to go over one per one"). Null on a row that is not a city of ours. */
  buildings = null,
  onUpgrade = null,
  onCancelUpgrade = null,
  onCancelSettle = null,
  /* What can be done with a cell we do not hold: found a city on it, at the price the deed will
     charge. It takes the same column the buildings do, because it is the same question asked of
     ground that has none yet. */
  settle = null,
  onSettle = null,
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  // Watching rather than playing: there is no action bar to be the first of two clicks, so a row
  // opens its city outright. See `clickCity` in the ui reducers.
  const reviewing = useSelector(selectIsReviewing)

  let cityInfo = null
  let extraParams = {}
  if (city) {
    const { flag } = getPlace({ row: city.row, column: city.col })
    // Never blank (owner's Q31): a cell outside any town falls back to "Puesto avanzado" /
    // "Campamento sin dueño", the same names the roster uses.
    const name = cellDisplayName({ row: city.row, column: city.col, held })

    // Hover is `.prod-row.clickable:hover` in the stylesheet now. It used to be React state driving
    // an inline background, which re-rendered the whole row twice per pass of the mouse.
    // focusCity, not clickCity (owner's ask, 2026-08-27): a row named after a city should take you
    // there — centre the map, select the cell (the action bar), and open the city view.
    extraParams = {
      onClick: () => {
        // A completed drag hands its mouse-up to the row; that is the drag ending, not a click.
        if (suppressClickUntil && Date.now() < suppressClickUntil.current) return
        dispatch(focusCity({ row: city.row, col: city.col, open: reviewing }))
      },
    }
    cityInfo = (
      <div className="coords-box">
        {/* The fire rides beside the flag, not after the name: the column is ~72px and a town
            called Makhachkala already fills two lines of it, so an inline badge wrapped onto a
            third line of its own and read as a stray emoji under the name.

            A 👑 stood here too, to say what the card's gold means. It is gone at the owner's call
            (2026-09-10: "remove the crown, it's enough with the spine"); the spine has since become
            a gold card (2026-10-07), which says it more loudly still. */}
        <div className="row-marks">
          {flag && <img src={flag} alt={t("game.troops.flagAlt")} />}
          {burning && (
            <span className="row-fire" title={t("game.troops.battleHere")} aria-label={t("game.troops.battleHere")}>
              🔥
            </span>
          )}
        </div>
        {/* The whole name on the title: it is clamped to two lines and a long one ellipsises, so
            this is where "San Carlos de Río Negro" is still readable. */}
        <div className="city-name p-mini" title={name}>
          {name}
        </div>
        <div className="coords">
          {city.col}-{city.row}
        </div>
        {/* Two different facts, and they were one word until the owner said so (2026-09-04): a
            fight happening *now*, and a march that has not landed yet. The fight is the 🔥 above and
            says nothing in words — a burning city is the one thing on this panel that does not need
            reading. Only the warning gets a line, because "something is coming" is not visible any
            other way. */}
        {underAttack && !burning && <div className="row-status">{t("game.troops.underAttack")}</div>}
        {/* The city's main building, under its coordinates and pressable (owner, 2026-09-10). What
            a player means by "how big is this city" is its command centre or its hatchery, so it
            stays in the place's own column rather than joining the rank on the right.

            Drawn as the same tile as the two on the right (owner, 2026-09-29: "so the user can
            understand it's also a building") — the plate, the level in the top-right notch, the
            price bottom left — and at the same width, `--row-tile-w`, so every building on the
            card is the same size. */}
        {mainBuilding && (
          <BuildingTile
            {...mainBuilding}
            main
            t={t}
            onUpgrade={onUpgrade}
            onCancelUpgrade={onCancelUpgrade}
            suppressClickUntil={suppressClickUntil}
          />
        )}
      </div>
    )
  }

  let productionInfo = null
  if (productionTime) {
    productionInfo = (
      <div className="production-info">
        <div className="prod-icon">⏱</div>
        <div className="prod-content">
          <div className="prod-label">{t("game.troops.rounds")}</div>
          <div className="prod-time">{productionTime}</div>
        </div>
      </div>
    )
  }

  /* The row's fourth column (owner, 2026-09-09). A city carries its three growth buildings, one
     section under the next; ground we merely stand on carries the one thing that can be done to
     it, which is to found a city on it — at the price the deed will charge, and pressing it pays.

     Drawn as part of the card rather than as chips on it: a hairline off the units, hairlines
     between the sections, and the card's own background. They are a column of the row, not three
     buttons somebody put there. */
  const sections = []
  if (buildings && buildings.length > 0) {
    buildings.forEach((building) => {
      sections.push(
        <BuildingTile
          key={building.code}
          {...building}
          t={t}
          onUpgrade={onUpgrade}
          onCancelUpgrade={onCancelUpgrade}
          suppressClickUntil={suppressClickUntil}
        />
      )
    })
  }
  if (settle) {
    sections.push(
      <button
        type="button"
        className={`row-section row-settle${settle.founding ? " upgrading" : settle.allowed ? "" : " blocked"}`}
        key="settle"
        disabled={!settle.founding && !settle.allowed}
        title={settle.founding ? t("city.cancelFoundingHint") : settle.reason || t("city.settle")}
        onClick={(event) => {
          event.stopPropagation()
          // The lift ending, not a press (see BuildingTile).
          if (suppressClickUntil && Date.now() < suppressClickUntil.current) return
          // A base on its way (owner, 2026-10-03) wears the building tiles' pick, and a press
          // takes the order back.
          if (settle.founding) onCancelSettle?.()
          else onSettle?.()
        }}
      >
        {settle.code ? (
          <img src={buildingArt[settle.code]} alt="" className="row-section-art" />
        ) : (
          <span className="row-section-flag" aria-hidden="true">
            🚩
          </span>
        )}
        <span className="row-section-text">
          {/* "BASE", not "asentarse" (owner, 2026-09-10): the verb did not fit a 90px tile, and
              what the press makes is a base — which is also what the picture behind it shows. No
              level here; there is nothing standing yet to have one. */}
          <span className="row-section-label">{t("game.troops.settleShort")}</span>
          {settle.founding ? (
            <span className="row-section-cost row-section-upgrading">
              <UnderConstruction className="row-section-construction" decorative />
            </span>
          ) : (
            <span className="row-section-cost">
              {settle.cost}
              {settle.currency}
            </span>
          )}
        </span>
      </button>
    )
  }

  return (
    <div
      className={`prod-row retro-card${city ? " clickable" : ""}${isCrown ? " crown" : ""}${
        burning ? " in-combat" : underAttack ? " under-attack" : ""
      }`}
      {...extraParams}
    >
      {cityInfo}
      {productionInfo}
      {/* Two of the row's four columns: three chips to a line, wrapping once. Five kinds of unit
          is the most a player can field, so every garrison fits without a scroller. */}
      <div className="prod-row-units">
        <UnitsCanvas unitsInfo={units} layout="grid" />
      </div>
      {sections.length > 0 && <div className="prod-row-side">{sections}</div>}
    </div>
  )
}

export default UnitsCanvasDetailed
